<!-- ReusableVideoPlayer.vue -->
<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import VolumeIcon from '~/icons/VolumeIcon.vue';

interface Props {
	// eslint-disable-next-line vue/require-default-prop
	src?: string;
	// eslint-disable-next-line vue/require-default-prop
	preview?: string;
	autoplay?: boolean;
	loop?: boolean;
	muted?: boolean;
	showMuteControl?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	autoplay: true,
	loop: true,
	muted: true,
	showMuteControl: true,
});

const emit = defineEmits<{
	(e: 'error', error: Event): void;
	(e: 'mute-change', isMuted: boolean): void;
	(e: 'play'): void;
	(e: 'pause'): void;
}>();

const videoEl = ref<HTMLVideoElement>();
const muted = ref(props.muted);
const showMuteIcon = ref(false);
const hasUserInteraction = ref(false);

// Public Methods
const play = async () => {
	try {
		await videoEl.value?.play();
		emit('play');
	} catch (error) {
		emit('error', error as Event);
	}
};

const pause = () => {
	videoEl.value?.pause();
	emit('pause');
};

const toggleMute = () => {
	muted.value = !muted.value;
	emit('mute-change', muted.value);
};

// Private Functions
const attemptAutoplay = async () => {
	if (!props.autoplay || !videoEl.value) return;

	try {
		if (props.muted || hasUserInteraction.value) {
			await play();
			showMuteIcon.value = true;
		}
	} catch (error) {
		console.debug('Autoplay blocked, waiting for interaction');
	}
};

const handleContainerClick = async () => {
	if (!hasUserInteraction.value) {
		hasUserInteraction.value = true;
		await attemptAutoplay();
	}
};

const handleError = (e: Event) => {
	emit('error', e);
};

// Watchers
watch(
	() => props.src,
	() => {
		attemptAutoplay();
	},
);

watch(
	() => props.muted,
	(newVal) => {
		muted.value = newVal;
	},
);

// Lifecycle
onMounted(() => {
	if (props.autoplay) attemptAutoplay();
});

// Expose public API
defineExpose({
	play,
	pause,
	toggleMute,
	videoElement: videoEl,
});
</script>
<template>
	<div class="relative w-full">
		<div
			class="relative w-full mx-auto min-h-[100px] lg:rounded-3xl bg-gray-200 flex items-center justify-center"
		>
			<video
				v-if="src"
				ref="videoEl"
				:src="src"
				class="w-fit lg:rounded-3xl bg-gray-200 content-center"
				:loop="loop"
				:muted="muted"
				:controls="false"
				:playsinline="true"
				:poster="preview"
				@loadedmetadata="attemptAutoplay"
				@error="handleError"
				@click="handleContainerClick"
			></video>

			<!-- Mute Control -->
			<div
				v-if="showMuteIcon"
				class="absolute inset-0 flex items-end justify-end p-md gap-md"
			>
				<button
					v-if="showMuteControl"
					class="z-10 rounded-full bg-black/50 p-8 text-white hover:bg-black/70"
					@click.stop.prevent="toggleMute"
				>
					<VolumeIcon v-if="muted" :muted="muted" />
					<VolumeIcon v-else :muted="muted" />
				</button>
			</div>
		</div>
	</div>
</template>
